import { type AllOrNothing } from '@src/types/utils';
import { Checkbox } from '@components/input/Checkbox.tsx';
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { MAX_NUMBER_OF_LOADING_SKELETONS } from '@src/config/constants';
import {
  type RequiredSortingPaginatedTableProps,
  type SelectionProps,
} from '@components/table/types';
import { Skeleton } from '@components/Skeleton.tsx';
import { classNames } from '@utils/classNames.ts';
import { times } from '@utils/general.ts';
import { useTranslation } from 'react-i18next';

export default function TableBody<
  T,
  AdditionalProps extends Record<string, unknown>,
>({
  data,
  loading,
  getId,
  columns,
  itemsPerPage,
  onRowClick,
  getRowLink,
  additionalProps,
  ...rest
}: Readonly<
  Pick<
    RequiredSortingPaginatedTableProps<T, AdditionalProps>,
    | 'columns'
    | 'itemsPerPage'
    | 'data'
    | 'loading'
    | 'getId'
    | 'additionalProps'
    | 'onRowClick'
    | 'getRowLink'
  > &
    AllOrNothing<SelectionProps>
>) {
  const rowIsSelected = useCallback(
    (rowData: T) => {
      return !!rest.selectedIds?.has(getId(rowData));
    },
    [getId, rest.selectedIds],
  );
  const previousNumberOfRows = useRef(
    Math.min(itemsPerPage, MAX_NUMBER_OF_LOADING_SKELETONS),
  );

  useEffect(() => {
    if (!loading) {
      previousNumberOfRows.current = Math.min(
        MAX_NUMBER_OF_LOADING_SKELETONS,
        Math.max(1, data.length),
      );
    }
  }, [data.length, loading]);

  const TdWrapper = useCallback(
    ({
      id,
      columnID,
      children,
    }: {
      id: string;
      children: ReactNode;
      columnID: string;
    }) => {
      if (columnID !== 'actions' && getRowLink) {
        return (
          <Link className="block h-full w-full" to={getRowLink(id)}>
            {children}
          </Link>
        );
      }
      return <Fragment children={children} />;
    },
    [getRowLink],
  );

  return (
    <tbody className="bg-white overflow-x-scroll block sm:table-row-group">
      {loading && (
        <TableContentPlaceholder
          columns={columns}
          rows={previousNumberOfRows.current}
        />
      )}
      {!loading && data.length === 0 && (
        <NoEntryPlaceholder numberOfColumns={columns.length} />
      )}
      {!loading &&
        data.map((rowData) => {
          const id = getId(rowData);
          return (
            <tr
              key={id}
              className={classNames(
                'animate-fade-in',
                'hover:bg-[#F9FBFD] border-b last:border-none border-blue-100',
                'block sm:table-row relative',
                'py-4 sm:py-0',
                rest.selectedIds?.has(id) ? 'bg-gray-50' : undefined,
                onRowClick && 'cursor-pointer',
              )}
              data-testid={`table-row-${id}`}
              onClick={(e) => onRowClick?.(rowData, e)}
            >
              {rest.selectedIds && (
                <td className="text-center align-middle pl-4 sm:w-12 h-full">
                  <Checkbox
                    data-testid="select-row-checkbox"
                    value={id}
                    checked={rowIsSelected(rowData)}
                    // This is needed to prevent the row click event from being triggered
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      rest.setSelectedIds(
                        e.target.checked
                          ? new Set([...rest.selectedIds, id])
                          : new Set(
                              Array.from(rest.selectedIds).filter(
                                (oldId) => oldId !== id,
                              ),
                            ),
                      );
                    }}
                  />
                </td>
              )}
              {columns.map((col) => {
                if (!col) {
                  return <TableCellPlaceholder />;
                }
                return (
                  <td
                    key={`${id}-${col.identifier}`}
                    className={classNames(
                      'sm:py-4 px-3 text-sm font-normal table-cell align-middle',
                      'relative block sm:table-cell',
                      rowIsSelected(rowData) && '!text-blue-500',
                      col.cellClassName,
                    )}
                  >
                    <TdWrapper id={id} columnID={col.identifier}>
                      {/* @ts-expect-error this is unsafe since additionalProps and rowData _might_ overlap. This should never happen */}
                      {col.renderCell({ rowData, ...additionalProps })}
                    </TdWrapper>
                  </td>
                );
              })}
            </tr>
          );
        })}
    </tbody>
  );
}

const TableContentPlaceholder = ({
  columns = [{}, {}],
  rows,
}: {
  columns: readonly unknown[];
  rows: number;
}) => {
  return times(
    <tr>
      {columns.map((_, index) => (
        <TableCellPlaceholder key={index} />
      ))}
    </tr>,
    rows,
  );
};

const NoEntryPlaceholder = ({
  numberOfColumns,
}: {
  numberOfColumns: number;
}) => {
  const { t } = useTranslation('table');
  return (
    <tr className="animate-fade-in">
      <td colSpan={numberOfColumns} className="py-4 px-3 text-sm">
        {t('noEntry')}
      </td>
    </tr>
  );
};

const TableCellPlaceholder = () => {
  return (
    <td className="py-4 px-3 text-sm">
      <Skeleton />
    </td>
  );
};
